import { useRef, useState } from "react";
import { Reveal } from "../Reveal";
import { ActivityLightbox } from "./ActivityLightbox";
import { ACTIVITY_GALLERY } from "./galleryData";

/** Masonry-style gallery of all 15 migrated Activities images + lightbox. */
export function ActivitiesGallery() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  function openAt(index: number) {
    returnFocusRef.current = triggerRefs.current[index] ?? null;
    setOpenIndex(index);
  }

  return (
    <section className="bg-background py-24 sm:py-28 lg:py-32" aria-labelledby="gallery-title">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <p className="eyebrow text-clay">Guest Moments</p>
          <h2
            id="gallery-title"
            className="mt-4 font-display text-3xl leading-tight sm:text-4xl lg:text-[2.9rem]"
          >
            Memories Made at Amigo Inn
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
            Take a look at gatherings, celebrations, meals, and memorable moments shared by guests
            at Amigo Inn &amp; RV Park.
          </p>
        </Reveal>

        <ul className="mt-14 columns-1 gap-4 sm:columns-2 sm:gap-5 lg:columns-3 lg:gap-6">
          {ACTIVITY_GALLERY.map((image, index) => (
            <Reveal
              as="li"
              key={image.id}
              delay={(index % 3) * 0.05}
              className="mb-4 break-inside-avoid sm:mb-5 lg:mb-6"
            >
              <button
                ref={(el) => {
                  triggerRefs.current[index] = el;
                }}
                type="button"
                onClick={() => openAt(index)}
                className="group media-zoom block w-full rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                aria-label={`Open image ${index + 1}: ${image.caption}`}
              >
                <img
                  src={image.src}
                  srcSet={image.srcSet}
                  sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw"
                  alt={image.alt}
                  width={image.width}
                  height={image.height}
                  loading="lazy"
                  decoding="async"
                  className="w-full rounded-sm object-cover"
                />
              </button>
            </Reveal>
          ))}
        </ul>
      </div>

      {openIndex !== null && (
        <ActivityLightbox
          images={ACTIVITY_GALLERY}
          index={openIndex}
          onClose={() => setOpenIndex(null)}
          onNavigate={setOpenIndex}
          returnFocusRef={returnFocusRef}
        />
      )}
    </section>
  );
}
